export function getNextRadiusOption(currentRadius, radiusOptions = []) {
  const current = Number(currentRadius);
  return radiusOptions
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > current)
    .sort((a, b) => a - b)[0] || null;
}

function pluralize(count, singular, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

// How many shops at a given radius would actually survive the user's current filters. The
// diagnostics report total / specialists / general_repair separately, and which one matters
// depends on the filters. Counting `total` regardless is what made "expand to see N shops"
// promise shops that a specialist-only filter then hid.
function countForFilters(item, { make, filterSpecialist, filterGeneral }) {
  if (!item) return 0;
  const total = Number(item.total || 0);
  const specialists = Number(item.specialists || 0);
  const general = Number(item.general_repair ?? item.generalRepair ?? 0);
  if (make && filterSpecialist && !filterGeneral) return specialists;
  if (make && !filterSpecialist && filterGeneral) return general;
  return total;
}

// Offer "expand the radius" only when a larger radius would actually surface more shops than
// are visible right now. Otherwise we'd keep inviting the user to expand (5 -> 10 -> 25 ...)
// all the way to the maximum while their filters hide every extra shop the wider search found.
function buildExpandRadiusAction({
  diagnostics,
  radiusMiles,
  radiusOptions,
  make,
  filterSpecialist,
  filterGeneral,
  visibleCount,
}) {
  const options = diagnostics?.radius_options || diagnostics?.radiusOptions || [];
  const current = Number(radiusMiles);

  // Without diagnostics we cannot tell whether expanding helps, so keep offering the next
  // radius - just without promising a count.
  if (!options.length) {
    const next = getNextRadiusOption(radiusMiles, radiusOptions);
    return next ? { type: 'expand_radius', label: `Expand to ${next} miles`, radiusMiles: String(next) } : null;
  }

  const filters = { make, filterSpecialist, filterGeneral };
  const specialistOnly = Boolean(make) && filterSpecialist && !filterGeneral;

  const useful = options
    .map((item) => ({ item, radius: Number(item?.radius_miles ?? item?.radiusMiles) }))
    .filter(({ radius }) => Number.isFinite(radius) && radius > current)
    .sort((a, b) => a.radius - b.radius)
    .filter(({ item }) => countForFilters(item, filters) > visibleCount);

  if (!useful.length) return null;

  // Honour the server's recommendation when it is one of the radii that genuinely helps
  // (it prefers radii that add specialists); otherwise take the smallest that helps.
  const recommended =
    diagnostics?.recommended_action?.type === 'expand_radius'
      ? Number(diagnostics.recommended_action.radius_miles)
      : null;
  const chosen = useful.find(({ radius }) => radius === recommended) || useful[0];

  const count = countForFilters(chosen.item, filters);
  const specialists = Number(chosen.item?.specialists || 0);
  let label = `Expand to ${chosen.radius} miles`;
  if (count > 0) {
    if (specialistOnly) {
      label = `${label} to see ${pluralize(count, `${make} specialist`)}`;
    } else {
      label = `${label} to see ${pluralize(count, 'shop')}`;
      if (make && specialists > 0) {
        label = `${label}, including ${pluralize(specialists, `${make} specialist`)}`;
      }
    }
  }

  return { type: 'expand_radius', label, radiusMiles: String(chosen.radius) };
}

export function buildShopSearchRecovery({
  shops = [],
  visibleShops = [],
  radiusMiles,
  radiusOptions = [],
  make = '',
  zipCode = '',
  // Shop Explorer has no explicit specialist toggle - when a make is chosen, specialists always
  // match there - so this defaults to true and only the car request page passes it through.
  filterSpecialist = true,
  filterGeneral = true,
  filterFavoritesOnly = false,
  sparseThreshold = 2,
  diagnostics = null,
} = {}) {
  const totalCount = Array.isArray(shops) ? shops.length : 0;
  const visibleCount = Array.isArray(visibleShops) ? visibleShops.length : 0;
  const trimmedMake = String(make || '').trim();
  const trimmedZip = String(zipCode || '').trim();
  const specialistCount = (shops || []).filter((shop) => shop?.is_specialist_for_make === true).length;
  const generalCount = (shops || []).filter((shop) => shop?.does_general_repair === true).length;
  const nextRadius = getNextRadiusOption(radiusMiles, radiusOptions);
  const radiusLabel = radiusMiles ? `${radiusMiles} miles` : 'this radius';
  const areaLabel = trimmedZip ? ` of ${trimmedZip}` : '';
  const actions = [];

  if (diagnostics?.zip_found === false) {
    return {
      severity: 'zip_not_found',
      title: trimmedZip ? `We could not locate ZIP ${trimmedZip}.` : 'We could not locate that ZIP code.',
      detail: 'Check the ZIP code or try a nearby city ZIP before expanding the search radius.',
      reason: 'This ZIP was not found in our search database.',
      actions: [],
    };
  }

  if (nextRadius) {
    const expandAction = buildExpandRadiusAction({
      diagnostics,
      radiusMiles,
      radiusOptions,
      make: trimmedMake,
      filterSpecialist,
      filterGeneral,
      visibleCount,
    });
    // null means no larger radius would reveal anything under the current filters.
    if (expandAction) actions.push(expandAction);
  }
  if (trimmedMake && !filterGeneral && generalCount > 0) {
    actions.push({ type: 'include_general', label: 'Include general repair shops' });
  }
  if (filterFavoritesOnly && totalCount > visibleCount) {
    actions.push({ type: 'show_all', label: 'Show all shops' });
  }

  if (totalCount === 0) {
    return {
      severity: 'empty',
      title: `No active shops found within ${radiusLabel}${areaLabel}.`,
      detail: trimmedMake
        ? `We could not find shops for ${trimmedMake} in the current search area. Expanding the radius is the fastest next step.`
        : 'Expanding the radius is the fastest next step.',
      reason: 'No active shops are currently in range for this ZIP and radius.',
      actions,
    };
  }

  if (visibleCount === 0) {
    let reason = `${pluralize(totalCount, 'shop')} ${totalCount === 1 ? 'is' : 'are'} in range, but current filters are hiding ${totalCount === 1 ? 'it' : 'them'}.`;
    if (filterFavoritesOnly) {
      reason = `${pluralize(totalCount, 'shop')} ${totalCount === 1 ? 'is' : 'are'} in range, but favorites-only is hiding ${totalCount === 1 ? 'it' : 'them'}.`;
    } else if (trimmedMake && specialistCount === 0 && generalCount > 0 && !filterGeneral) {
      reason = `No ${trimmedMake} specialists were found within ${radiusLabel}, but ${pluralize(generalCount, 'general repair shop')} ${generalCount === 1 ? 'is' : 'are'} nearby.`;
    } else if (trimmedMake && specialistCount === 0) {
      reason = `No ${trimmedMake} specialists were found within ${radiusLabel}.`;
    }

    return {
      severity: 'filtered_empty',
      title: 'No shops match the current filters.',
      detail: reason,
      reason,
      actions,
    };
  }

  if (visibleCount < sparseThreshold) {
    const detail = trimmedMake && specialistCount === 0 && generalCount > 0
      ? `No ${trimmedMake} specialists are listed within ${radiusLabel}; the current match is general repair.`
      : `Only ${pluralize(visibleCount, 'shop')} ${visibleCount === 1 ? 'matches' : 'match'} this search. For better response odds, select 2-4 shops when possible.`;

    return {
      severity: 'sparse',
      title: `Only ${pluralize(visibleCount, 'shop')} shown.`,
      detail,
      reason: detail,
      actions,
    };
  }

  return null;
}
